/**
 * Free-item override (?free-item=butter) — display half.
 *
 * wg-free-item.liquid has already resolved the key from the URL and stamped
 * <html data-wg-free-item="…"> before first paint; this file applies the copy that implies.
 *
 * Scoped to the visitor, not the page: arriving on a campaign URL joins a cohort held in
 * the `wg_free_item` cookie, and wg-free-item.liquid stamps <html> from that cookie on every
 * later page. Nothing here reads the URL or the cookie itself — it only applies whatever the
 * snippet resolved, so this file is unchanged by the page-scoped → visitor-scoped move.
 *
 * The homepage carousel takes per-plan slides (applySlides); the homepage "Build your box"
 * hero takes the item-label slot and a tagged promo line.
 *
 * Everything it writes comes from window.WG_FREE_ITEM (the content map in the snippet),
 * so the override copy is edited in exactly one place.
 */
(function () {
  var config = window.WG_FREE_ITEM;
  if (!config) return;

  /* -------------------------------------------------------------- copy overrides */

  var setText = function (el, value) {
    if (!el || !value) return;
    /* Slide titles wrap their text in <motion-element>; write into the innermost
       wrapper so the entrance animation still has an element to act on. */
    var target = el.querySelector('motion-element') || el;
    target.textContent = value;
  };

  var setHtml = function (el, value) {
    if (!el || !value) return;
    var target = el.matches('motion-element') ? el : el.querySelector('motion-element') || el;
    target.innerHTML = value;
  };

  /* Sections that opt in by tagging their own nodes (hero-croissants-promo,
     footer-croissants). Attribute-driven rather than selector-driven so a section can be
     restyled without silently dropping out of the override. */
  var SLOTS = {
    'hero-title': function (el) { setText(el, config.hero && config.hero.title) },
    'hero-subtitle': function (el) { setHtml(el, config.hero && config.hero.subtitle) },
    'hero-image': function (el) {
      /* Prefers the campaign's own hero shot, falling back to the product photo for an
         override that hasn't been given one. */
      var src = config.heroImage || config.image;
      if (!src) return;
      el.setAttribute('src', src);
      /* The section renders a responsive srcset for ITS image; leaving it would let the
         browser pick a source we just replaced. */
      el.removeAttribute('srcset');
    },
    'hero-email-button': function (el) {
      var text = config.hero && config.hero.emailButtonText;
      if (!text) return;
      var strong = el.querySelector('strong');
      (strong || el).textContent = text;
    },
    'hero-skip': function (el) { if (config.hero && config.hero.skipText) el.textContent = config.hero.skipText },
    'footer-heading': function (el) { setText(el, config.footer && config.footer.heading) },
    'footer-text': function (el) { setHtml(el, config.footer && config.footer.text) },
    /* One- or two-word name for tight spots, like the homepage hero's locked box slot. */
    'item-label': function (el) { setText(el, config.label) }
  };

  var applySlots = function (root) {
    Object.keys(SLOTS).forEach(function (slot) {
      root.querySelectorAll('[data-wg-free-item-slot="' + slot + '"]').forEach(function (el) {
        if (el.dataset.wgFreeItemApplied === '1') return;
        el.dataset.wgFreeItemApplied = '1';
        SLOTS[slot](el);
      });
    });
  };

  /* Promo copy that lives in generic sections (rich-text, layered-images-with-text …),
     where there is no free-item-specific setting to override. Phrase replacement is
     scoped to elements explicitly tagged data-wg-free-item-copy, never the whole page:
     an unscoped sweep would also rewrite recipe names and reviews that mention
     croissants, which are still real products in the box. */
  var applyCopy = function (root) {
    var rules = config.copy || [];
    if (!rules.length) return;
    /* Longest phrase first so "Free Croissants For Life" is not half-consumed by the
       shorter "Free Croissants" rule. Case-sensitive: the map lists the casings it
       expects, so replacements read naturally wherever they land in a sentence. */
    var ordered = rules.slice().sort(function (a, b) { return b[0].length - a[0].length });
    root.querySelectorAll('[data-wg-free-item-copy]').forEach(function (el) {
      if (el.dataset.wgFreeItemApplied === '1') return;
      el.dataset.wgFreeItemApplied = '1';
      var html = el.innerHTML;
      ordered.forEach(function (rule) {
        html = html.replace(new RegExp(rule[0].replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), rule[1]);
      });
      if (html !== el.innerHTML) el.innerHTML = html;
    });
  };

  /* Homepage carousel. Keyed by the slide's diet_type rather than by a fixed slot name,
     because each plan gets its own title and shot from config.slides. A slide whose
     diet_type has no entry (gifting) is skipped, so it keeps its own content. */
  var applySlides = function (root) {
    var slides = config.slides;
    if (!slides) return;

    root.querySelectorAll('[data-wg-free-item-slide-title]').forEach(function (el) {
      var slide = slides[el.getAttribute('data-wg-free-item-slide-title')];
      if (!slide || !slide.title || el.dataset.wgFreeItemApplied === '1') return;
      el.dataset.wgFreeItemApplied = '1';
      el.textContent = slide.title;
    });

    /* Description is HTML (an .rte block), so it is written as innerHTML rather than text.
       A slide with no description in the map keeps its own — the gluten-free slide's copy
       is item-agnostic, so only Variety needs replacing. */
    root.querySelectorAll('[data-wg-free-item-slide-desc]').forEach(function (el) {
      var slide = slides[el.getAttribute('data-wg-free-item-slide-desc')];
      if (!slide || !slide.description || el.dataset.wgFreeItemApplied === '1') return;
      el.dataset.wgFreeItemApplied = '1';
      el.innerHTML = slide.description;
    });

    /* The <source> children carry responsive srcsets for the section's OWN image and
       outrank the <img>, so they go before a swapped src can take effect. width/height
       stay put, leaving the slide's reserved space and aspect crop unchanged.

       A slide with its own mobile shot (imageMobile) gets a single <source> back, on
       the section's own 750px breakpoint, so phones load the mobile crop and nothing
       else — the same mechanism the section uses for its background_mobile setting. */
    root.querySelectorAll('[data-wg-free-item-slide-bg]').forEach(function (el) {
      var slide = slides[el.getAttribute('data-wg-free-item-slide-bg')];
      if (!slide || !slide.image || el.dataset.wgFreeItemApplied === '1') return;
      el.dataset.wgFreeItemApplied = '1';
      el.querySelectorAll('source').forEach(function (source) { source.remove(); });
      var img = el.querySelector('img');
      if (!img) return;
      if (slide.imageMobile) {
        var source = document.createElement('source');
        source.setAttribute('media', '(max-width: 750px)');
        source.setAttribute('srcset', slide.imageMobile);
        el.insertBefore(source, img);
      }
      img.setAttribute('src', slide.image);
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      if (config.name) img.setAttribute('alt', config.name);
    });
  };

  /* ------------------------------------------------------------------------- run */

  var apply = function (root) {
    applySlots(root);
    applySlides(root);
    applyCopy(root);
  };

  var start = function () {
    apply(document);

    /* Sections render late in the theme editor and the box builder mounts asynchronously,
       so re-apply on mutation. Everything above is idempotent (data-wg-free-item-applied),
       making this cheap. */
    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () {
        pending = null;
        apply(document);
      }, 100);
    }).observe(document.documentElement, { childList: true, subtree: true });
  };

  /* Deferred, so the document is parsed by the time this runs — apply immediately rather
     than waiting for DOMContentLoaded, which would let the croissant copy paint first. */
  if (document.body) {
    start();
  } else {
    document.addEventListener('DOMContentLoaded', start);
  }
})();
